'use client'

import { useCallback, useState, type ReactNode } from 'react'

import { Button } from '@/app/components/atoms/Button'
import {
  IconArrowRight,
  IconCloseCircle,
  IconPerson,
  IconShoppingBag,
} from '@/app/components/atoms/Icons'
import { SideSheet } from '@/app/components/atoms/SideSheet'
import { cx } from '@/app/lib/utils'

import styles from './AccountDrawer.module.scss'
import {
  ACCOUNT_DRAWER_GREETING,
  ACCOUNT_DRAWER_TITLE,
  ACCOUNT_TRIGGER_LABEL,
  ACCOUNT_TRIGGER_LOGGED_IN_LABEL,
  CTA_ICON_SIZE,
  MY_ACCOUNT_LABEL,
  MY_ACCOUNT_URL,
  MY_PURCHASES_LABEL,
  MY_PURCHASES_URL,
  ROW_ICON_SIZE,
  SIGN_IN_LABEL,
  SIGN_OUT_LABEL,
} from './constants'

interface AccountDrawerProps {
  /** Contenido del botón (icono de persona + punto de sesión o saludo). */
  readonly children: ReactNode
  readonly triggerClassName?: string
  /**
   * Nombre accesible del botón. Si el botón muestra texto (p. ej. el saludo),
   * debe empezar por ese texto (WCAG 2.5.3). Por defecto, según la sesión,
   * `ACCOUNT_TRIGGER_LABEL` o `ACCOUNT_TRIGGER_LOGGED_IN_LABEL`.
   */
  readonly triggerLabel?: string
  readonly isLoggedIn: boolean
  /** Nombre del usuario con sesión: el título pasa a «Hola, {nombre}». */
  readonly userName?: string | null
  /** Lanza el login (mismo flujo de siempre: `signInAction` + redirección). */
  readonly onSignIn: () => void
  /** Lanza el logout (`signOutAction` + redirección). */
  readonly onSignOut: () => void
}

/**
 * Panel lateral «Accesos» de la cuenta, igual que el de elcorteingles.es: al
 * pulsar el icono de persona se abre por la derecha (pantalla completa en
 * móvil, 480px desde tablet) con el CTA «Iniciar sesión o crear cuenta» (sin
 * sesión) y los accesos al área privada. La diferencia con la referencia son
 * los enlaces: solo «Mis compras» y «Mi cuenta», los propios de Entradas
 * (origen `ticketingStore`). Con sesión, el CTA de login se sustituye por
 * «Cerrar sesión» al final de la lista, en rojo con el círculo con aspa y sin
 * flecha, igual que en la referencia.
 *
 * El login y el logout no cambian: el panel solo se cierra y delega en
 * `onSignIn`/`onSignOut` (server action + redirección, ver `Header`). El
 * contrato modal (foco, Escape, scrim, bloqueo de scroll) lo da `SideSheet`.
 */
export function AccountDrawer({
  children,
  triggerClassName,
  triggerLabel,
  isLoggedIn,
  userName,
  onSignIn,
  onSignOut,
}: AccountDrawerProps) {
  const [open, setOpen] = useState(false)
  // Con sesión y nombre, «Hola, {nombre}» como en elcorteingles.es; si no,
  // «Accesos» (sin sesión, o con sesión pero sin la cookie del nombre).
  const greeting = isLoggedIn && userName ? `${ACCOUNT_DRAWER_GREETING}, ${userName}` : null
  const close = useCallback(() => setOpen(false), [])

  const handleSignIn = () => {
    close()
    onSignIn()
  }

  const handleSignOut = () => {
    close()
    onSignOut()
  }

  const links = [
    { href: MY_PURCHASES_URL, label: MY_PURCHASES_LABEL, icon: <IconShoppingBag size={ROW_ICON_SIZE} /> },
    { href: MY_ACCOUNT_URL, label: MY_ACCOUNT_LABEL, icon: <IconPerson size={ROW_ICON_SIZE} /> },
  ]

  return (
    <>
      <button
        type="button"
        aria-label={
          triggerLabel ?? (isLoggedIn ? ACCOUNT_TRIGGER_LOGGED_IN_LABEL : ACCOUNT_TRIGGER_LABEL)
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        className={triggerClassName}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>

      <SideSheet
        open={open}
        onClose={close}
        title={greeting ?? ACCOUNT_DRAWER_TITLE}
        // El saludo va más grande que «Accesos» (24px frente a 20px), como en
        // elcorteingles.es.
        titleSize={greeting ? 'l' : 'm'}
        size="m"
        variant="plain"
      >
        <div className={styles.body}>
          {!isLoggedIn && (
            <div className={styles.cta}>
              <Button
                variant="filled"
                shape="rounded"
                size="s"
                trailingIcon={<IconArrowRight size={CTA_ICON_SIZE} />}
                onClick={handleSignIn}
              >
                {SIGN_IN_LABEL}
              </Button>
            </div>
          )}

          <ul className={styles.list}>
            {links.map((link) => (
              <li key={link.href} className={styles.item}>
                <span className={styles.icon} aria-hidden="true">
                  {link.icon}
                </span>
                <a href={link.href} className={styles.row}>
                  {link.label}
                  <IconArrowRight size={ROW_ICON_SIZE} className={styles.arrow} />
                </a>
              </li>
            ))}
            {isLoggedIn && (
              <li className={cx(styles.item, styles.signOut)}>
                <span className={styles.icon} aria-hidden="true">
                  <IconCloseCircle size={ROW_ICON_SIZE} />
                </span>
                <button type="button" className={styles.row} onClick={handleSignOut}>
                  {SIGN_OUT_LABEL}
                </button>
              </li>
            )}
          </ul>
        </div>
      </SideSheet>
    </>
  )
}
